import { type Scope, createDomain, fork, sample } from 'effector';
import { signout } from '@features/common';
import { propertyTypesApi } from './api';
import {
  PropertyTypePayload,
  PropertyTypeResponse,
  Props,
  PropertyTypeItem,
} from './types';

export const ControlDomain = createDomain();

export const scopes: { [key: string]: Scope } = {};

export const createScope = (name: string) => {
  if (scopes[name]) return scopes[name];

  scopes[name] = fork(ControlDomain);

  return scopes[name];
};

export const getPropertyTypes = ControlDomain.createEvent<Props>();

export const $data = ControlDomain.createStore<PropertyTypeItem[]>([]);
export const $pending = ControlDomain.createStore<boolean>(false);

const fxGetPropertyTypes = ControlDomain.createEffect<
  PropertyTypePayload,
  PropertyTypeResponse,
  Error
>().use(propertyTypesApi.getPropertyTypes);

$data
  .on(fxGetPropertyTypes.done, (state, { result }) => {
    if (!Array.isArray(result?.types)) {
      return state;
    }

    return result.types.map((type) => ({
      id: type.id,
      label: type?.title ?? '',
      name: type?.parent_type_title ? type.parent_type_title : (type?.name ?? ''),
      picture: type?.picture ?? null,
    }));
  })
  .reset(signout);

$pending.on(fxGetPropertyTypes.pending, (_, pending) => pending).reset(signout);

sample({
  clock: getPropertyTypes,
  filter: ({ onlyActual, parentAreaId }) => (onlyActual ? Boolean(parentAreaId) : true),
  fn: ({
    withClientType,
    mode,
    onlyActual,
    parentAreaId,
    includeClientType,
    showActiveClientType,
    scope,
    isAvailable,
    forAreaId,
  }) => {
    const payload: PropertyTypePayload = {};

    if (withClientType) {
      payload.with_client_type = Number(withClientType);
    }

    if (mode) {
      payload.mode = mode;
    }

    if (Array.isArray(scope) && scope.length > 0) {
      payload.scope = scope;
    }

    if (forAreaId) {
      payload.for_area_id = forAreaId;
    }

    if (typeof isAvailable === 'boolean') {
      payload.is_available = Number(isAvailable);
    }

    if (onlyActual && parentAreaId) {
      payload.only_actual = Number(onlyActual);
      payload.parent_area_id = parentAreaId;
    }

    if (typeof includeClientType === 'boolean') {
      payload.include_client_type = Number(includeClientType);
    }

    if (typeof showActiveClientType === 'boolean') {
      payload.show_active_client_type = Number(showActiveClientType);
    }

    return payload;
  },
  target: fxGetPropertyTypes,
});
